import { useCallback, useEffect, useRef, useState } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "../ui/modal.tsx";
import { useAuth } from "../../auth/authContext.tsx";
import { useToast } from "../../hooks/useToast.ts";
import { createSharedLink, type SharedLinkData } from "../../api/sharedLink.ts";
import type { InputParam } from "../../types/grid.ts";
import styles from "./dataGrid.module.scss";

interface ShareDialogProps {
  name: string;
  filters: InputParam[];
  onClose: () => void;
}

export function ShareDialog({ name, filters, onClose }: ShareDialogProps) {
  const { session } = useAuth();
  const { toast } = useToast();
  const [link, setLink] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [copied, setCopied] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Auto-create on mount. Guarded with a ref so StrictMode's double-mount
  // doesn't fire the API call twice.
  const didCreate = useRef(false);
  useEffect(() => {
    if (didCreate.current) return;
    didCreate.current = true;
    setCreating(true);
    // Build page state blob with filters for this grid (raw InputParam[] format)
    const state: Record<string, unknown> = {};
    if (filters.length > 0) state[`f.${name}`] = filters;
    const data: SharedLinkData = { page: name, state };
    const endpointID = session?.ActiveEndPointID ?? 0;
    createSharedLink(endpointID, JSON.stringify(data))
      .then(result => {
        if (result.bError || !result.oData) {
          toast(result.sMessage ?? "Failed to create share link", "error");
          onClose();
        } else {
          setLink(`${window.location.origin}/s/${result.oData}`);
        }
      })
      .catch(() => {
        toast("Failed to create share link", "error");
        onClose();
      })
      .finally(() => setCreating(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-select input when link is ready
  useEffect(() => {
    if (link) inputRef.current?.select();
  }, [link]);

  const handleCopy = useCallback(async () => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback: select the input so user can Ctrl+C
      inputRef.current?.select();
    }
  }, [link]);

  return (
    <Modal open onClose={onClose}>
      <ModalHeader title="Share Link" onClose={onClose} />
      <ModalBody>
        {creating && <div style={{ padding: "var(--space-4)", textAlign: "center" }}>Creating link...</div>}
        {link && (
          <div style={{ display: "flex", gap: "var(--space-2)", alignItems: "center" }}>
            <input
              ref={inputRef}
              type="text"
              value={link}
              readOnly
              className={styles.exportSelect}
              style={{ flex: 1, fontFamily: "var(--font-family-input-mono)", fontSize: "var(--text-xs)" }}
              onClick={e => (e.target as HTMLInputElement).select()}
            />
            <button className={styles.toolbarBtnPrimary} onClick={handleCopy}>
              {copied ? "Copied" : "Copy"}
            </button>
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <button className={styles.toolbarBtn} onClick={onClose}>Close</button>
      </ModalFooter>
    </Modal>
  );
}
